import React from 'react';
import { Check } from '../../shared/components/Icons';
import { InfoTooltip } from '../../shared/components/InfoTooltip';
import { SUBJECT_COLOURS } from '../../shared/constants/subjects';

function Toggle({ checked, onChange }) {
  return (
    <label className="settings-toggle">
      <input type="checkbox" checked={checked} onChange={onChange} />
      <span className="settings-toggle-track" />
    </label>
  );
}

export default function AppearanceSettings({
  theme, onThemeChange,
  settings, setSettings, saveSetting, saved,
  PLANNER_COLORS, DASH_TOGGLES,
  confThreshRed, setConfThreshRed, confThreshGreen, setConfThreshGreen,
  dashMetricLayout, setDashMetricLayout, plannerSessionColours, setPlannerSessionColours,
  showMcqMilestone, setShowMcqMilestone,
}) {
  return (
    <>
      <div className="settings-section">
        <div className="settings-section-title">Appearance</div>
        <div className="settings-row" style={{ flexDirection: 'column', alignItems: 'flex-start', gap: 12 }}>
          <span className="settings-row-label">Colour theme</span>
          <div className="theme-picker">
            {[
              {
                key: 'default',
                label: 'Default',
                bg: '#ffffff',
                surface: '#f8fafc',
                text: '#0f172a',
                muted: '#64748b',
                accent: '#3b82f6',
              },
              {
                key: 'warm',
                label: 'Warm',
                bg: '#FAF8F5',
                surface: '#FFFDF9',
                text: '#2C2520',
                muted: '#7C7268',
                accent: '#3B82F6',
              },
              {
                key: 'dark',
                label: 'Dark',
                bg: '#0f172a',
                surface: '#1E293B',
                text: '#E2E8F0',
                muted: '#94A3B8',
                accent: '#60A5FA',
              },
            ].map(t => (
              <button
                key={t.key}
                className={`theme-card${theme === t.key ? ' theme-card--active' : ''}`}
                onClick={() => onThemeChange && onThemeChange(t.key)}
                style={{ background: t.bg }}
                aria-label={`${t.label} theme`}
                type="button"
              >
                <div className="theme-card-preview" style={{ background: t.surface, borderColor: t.key === 'dark' ? '#334155' : t.key === 'warm' ? '#E8E4DF' : '#e2e8f0' }}>
                  <div className="theme-card-topic-row">
                    <span className="theme-card-topic-bar" style={{ background: SUBJECT_COLOURS['Business Law and Practice'] }} />
                    <div className="theme-card-topic-lines">
                      <span className="theme-card-line theme-card-line--title" style={{ background: t.text }} />
                      <span className="theme-card-line theme-card-line--sub" style={{ background: t.muted }} />
                    </div>
                    <span className="theme-card-conf-dot" style={{ background: '#16a34a' }} />
                  </div>
                  <div className="theme-card-topic-row">
                    <span className="theme-card-topic-bar" style={{ background: SUBJECT_COLOURS['Contract Law'] }} />
                    <div className="theme-card-topic-lines">
                      <span className="theme-card-line theme-card-line--title" style={{ background: t.text }} />
                      <span className="theme-card-line theme-card-line--sub" style={{ background: t.muted }} />
                    </div>
                    <span className="theme-card-conf-dot" style={{ background: '#d97706' }} />
                  </div>
                </div>
                <span className="theme-card-label" style={{ color: t.text }}>{t.label}</span>
                {theme === t.key && (
                  <span className="theme-card-check" style={{ background: t.accent }}>
                    <svg width="10" height="10" viewBox="0 0 10 10" fill="none">
                      <path d="M2 5l2.5 2.5 3.5-4" stroke="#fff" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  </span>
                )}
              </button>
            ))}
          </div>
        </div>
      </div>

      <div className="settings-section">
        <div className="settings-section-title">Planner colours</div>
        {PLANNER_COLORS.map(({ key, label, default: def }) => (
          <div key={key} className="settings-row">
            <div className="settings-row-left">
              <span className="settings-row-label">{label}</span>
            </div>
            <div className="settings-row-right" style={{ gap: 10, alignItems: 'center' }}>
              <label className="settings-color-wrap">
                <input
                  type="color"
                  value={settings[key] || def}
                  onChange={e => {
                    setSettings(prev => ({ ...prev, [key]: e.target.value }));
                    saveSetting(key, e.target.value);
                  }}
                />
                <span className="settings-color-swatch" style={{ background: settings[key] || def }} />
              </label>
              <span style={{ fontFamily: 'var(--mono)', fontSize: 11, color: 'var(--hint)', lineHeight: 1, minWidth: 64 }}>
                {settings[key] || def}
              </span>
              <button
                className="settings-action-btn"
                style={{ padding: '2px 8px', fontSize: 11 }}
                disabled={!settings[key] || settings[key] === def}
                onClick={() => { setSettings(prev => ({ ...prev, [key]: def })); saveSetting(key, def); }}
              >
                Reset
              </button>
            </div>
          </div>
        ))}
      </div>

      <div className="settings-section">
        <div className="settings-section-title">Dashboard panels</div>
        {DASH_TOGGLES.map(({ key, label, desc }) => (
          <div key={key} className="settings-row">
            <div className="settings-row-left">
              <span className="settings-row-label">{label}</span>
              {desc && <span className="settings-row-key">{desc}</span>}
            </div>
            <div className="settings-row-right">
              <Toggle
                checked={(settings[key] ?? '1') === '1'}
                onChange={e => {
                  const val = e.target.checked ? '1' : '0';
                  setSettings(prev => ({ ...prev, [key]: val }));
                  saveSetting(key, val);
                }}
              />
            </div>
          </div>
        ))}
      </div>

      <div className="settings-section">
        <div className="settings-section-title">Confidence thresholds</div>
        <div className="settings-row">
          <div className="settings-row-left">
            <span className="settings-row-label">Needs work threshold <InfoTooltip text="Topics below this confidence level are marked as needs work and shown in red across the app." /></span>
            <span className="settings-row-key">Topics below this confidence level appear red</span>
          </div>
          <div className="settings-row-right" style={{ gap: 8, flexDirection: 'column', alignItems: 'flex-end' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, width: '100%', justifyContent: 'flex-end' }}>
              <input type="range" min="0" max="100" step="5"
                value={Math.round(parseFloat(confThreshRed) * 100)}
                onChange={e => {
                  const decimal = (parseInt(e.target.value) / 100).toFixed(2);
                  if (parseFloat(decimal) >= parseFloat(confThreshGreen)) return;
                  setConfThreshRed(decimal);
                  saveSetting('confidence_threshold_red', decimal);
                }}
                style={{ width: 120 }}
              />
              <span style={{ fontFamily: 'var(--mono)', fontSize: 12, color: 'var(--muted)', minWidth: 36, textAlign: 'right' }}>
                {Math.round(parseFloat(confThreshRed) * 100)}%
              </span>
            </div>
            {saved['confidence_threshold_red'] && <span className="settings-inline-saved"><Check size={12} /> Saved</span>}
          </div>
        </div>
        <div className="settings-row">
          <div className="settings-row-left">
            <span className="settings-row-label">Strong threshold <InfoTooltip text="Topics above this confidence level are marked as strong and shown in green. Topics between your needs-work and strong thresholds show as moderate (amber)." /></span>
            <span className="settings-row-key">Topics above this confidence level appear green. Between the two thresholds = amber.</span>
          </div>
          <div className="settings-row-right" style={{ gap: 8, flexDirection: 'column', alignItems: 'flex-end' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, width: '100%', justifyContent: 'flex-end' }}>
              <input type="range" min="0" max="100" step="5"
                value={Math.round(parseFloat(confThreshGreen) * 100)}
                onChange={e => {
                  const decimal = (parseInt(e.target.value) / 100).toFixed(2);
                  if (parseFloat(decimal) <= parseFloat(confThreshRed)) return;
                  setConfThreshGreen(decimal);
                  saveSetting('confidence_threshold_green', decimal);
                }}
                style={{ width: 120 }}
              />
              <span style={{ fontFamily: 'var(--mono)', fontSize: 12, color: 'var(--muted)', minWidth: 36, textAlign: 'right' }}>
                {Math.round(parseFloat(confThreshGreen) * 100)}%
              </span>
            </div>
            {saved['confidence_threshold_green'] && <span className="settings-inline-saved"><Check size={12} /> Saved</span>}
          </div>
        </div>
      </div>

      <div className="settings-section">
        <div className="settings-section-title">Layout &amp; display</div>
        <div className="settings-row">
          <div className="settings-row-left">
            <span className="settings-row-label">Dashboard metrics layout</span>
            <span className="settings-row-key">How metric cards display on your dashboard</span>
          </div>
          <div className="settings-row-right" style={{ flexDirection: 'column', alignItems: 'flex-start', gap: 0 }}>
            {[
              { value: 'single', label: 'Single row' },
              { value: 'grouped', label: 'Grouped (3+2)' },
            ].map(opt => (
              <label key={opt.value} className="settings-radio-label">
                <input
                  type="radio"
                  name="dashboard_metric_layout"
                  value={opt.value}
                  checked={dashMetricLayout === opt.value}
                  onChange={() => { setDashMetricLayout(opt.value); saveSetting('dashboard_metric_layout', opt.value); }}
                />
                <span className="settings-radio-text">
                  <span className="settings-radio-name">{opt.label}</span>
                </span>
              </label>
            ))}
            {saved['dashboard_metric_layout'] && <span className="settings-inline-saved"><Check size={12} /> Saved</span>}
          </div>
        </div>
        <div className="settings-row">
          <div className="settings-row-left">
            <span className="settings-row-label">Planner colour categories</span>
            <span className="settings-row-key">How many colour categories the planner uses</span>
          </div>
          <div className="settings-row-right" style={{ flexDirection: 'column', alignItems: 'flex-start', gap: 0 }}>
            {[
              { value: 'simplified', label: 'Simplified (5 categories)' },
              { value: 'detailed', label: 'Detailed (8 categories)' },
            ].map(opt => (
              <label key={opt.value} className="settings-radio-label">
                <input
                  type="radio"
                  name="planner_session_colours"
                  value={opt.value}
                  checked={plannerSessionColours === opt.value}
                  onChange={() => { setPlannerSessionColours(opt.value); saveSetting('planner_session_colours', opt.value); }}
                />
                <span className="settings-radio-text">
                  <span className="settings-radio-name">{opt.label}</span>
                </span>
              </label>
            ))}
            {saved['planner_session_colours'] && <span className="settings-inline-saved"><Check size={12} /> Saved</span>}
          </div>
        </div>
        <div className="settings-row">
          <div className="settings-row-left">
            <span className="settings-row-label">Show MCQ milestone bar</span>
            <span className="settings-row-key">Show the MCQ progress bar on the dashboard</span>
          </div>
          <div className="settings-row-right">
            <Toggle
              checked={showMcqMilestone === '1'}
              onChange={e => {
                const val = e.target.checked ? '1' : '0';
                setShowMcqMilestone(val);
                saveSetting('show_mcq_milestone', val);
              }}
            />
            {saved['show_mcq_milestone'] && <span className="settings-inline-saved" style={{ marginLeft: 8 }}><Check size={12} /> Saved</span>}
          </div>
        </div>
      </div>
    </>
  );
}
